import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { useGetProductsQuery, useGetProductQuery } from '../services/api';
import { ProductFilters } from '../types';
import { setSearchQuery } from '../store/uiSlice';
import { fetchRuntimeConfig } from '../services/config';

/**
 * Products ViewModel - Encapsulates product browsing and filtering logic
 * Follows MVVM pattern where this acts as the ViewModel layer
 */
export const useProductsViewModel = () => {
  const dispatch = useDispatch();
  const { searchQuery } = useSelector((state: RootState) => state.ui);
  
  // State for region_id from runtime config
  const [regionId, setRegionId] = useState<string | undefined>();

  // Local state for filters
  const [filters, setFilters] = useState<ProductFilters>({
    search: searchQuery,
    sortBy: 'created_at',
  });

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
  });

  // Load region_id from runtime config
  useEffect(() => {
    fetchRuntimeConfig().then((config) => {
      if (config.medusa.regionId) {
        setRegionId(config.medusa.regionId);
      }
    }).catch((error) => {
      console.error('Failed to load region from runtime config:', error);
    });
  }, []);

  // API query parameters
  const queryParams = useMemo(() => ({
    offset: pagination.offset,
    limit: pagination.limit,
    q: filters.search,
    category_id: filters.category ? [filters.category] : undefined,
    region_id: regionId,
  }), [pagination, filters, regionId]);

  // Fetch products
  const {
    data: productsData,
    isLoading,
    error,
    refetch,
  } = useGetProductsQuery(queryParams);

  const products = productsData?.products || [];
  const totalCount = productsData?.count || 0;

  // Search functionality
  const updateSearch = useCallback((query: string) => {
    dispatch(setSearchQuery(query));
    setFilters(prev => ({ ...prev, search: query }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  }, [dispatch]);

  // Filter functionality
  const updateFilters = useCallback((newFilters: Partial<ProductFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  }, []);

  // Pagination functionality
  const loadMore = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, []);

  // Sort products locally
  const sortedProducts = useMemo(() => {
    if (!products.length) return [];

    const sorted = [...products];
    
    switch (filters.sortBy) {
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'price_asc':
        return sorted.sort((a, b) => {
          const priceA = a.variants[0]?.calculated_price?.calculated_amount || 0;
          const priceB = b.variants[0]?.calculated_price?.calculated_amount || 0;
          return priceA - priceB;
        });
      case 'price_desc':
        return sorted.sort((a, b) => {
          const priceA = a.variants[0]?.calculated_price?.calculated_amount || 0;
          const priceB = b.variants[0]?.calculated_price?.calculated_amount || 0;
          return priceB - priceA;
        });
      case 'created_at':
      default:
        return sorted.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }
  }, [products, filters.sortBy]);

  // Filter products locally by price range if set
  const filteredProducts = useMemo(() => {
    if (!filters.priceRange) return sortedProducts;

    return sortedProducts.filter(product => {
      const price = product.variants[0]?.calculated_price?.calculated_amount || 0;
      return price >= filters.priceRange!.min && price <= filters.priceRange!.max;
    });
  }, [sortedProducts, filters.priceRange]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      sortBy: 'created_at',
    });
    dispatch(setSearchQuery(''));
    resetPagination();
  }, [dispatch, resetPagination]);

  // Pagination info
  const hasNextPage = pagination.offset + pagination.limit < totalCount;
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return {
    // Data
    products: filteredProducts,
    totalCount,
    isLoading,
    error,

    // Filters
    filters,
    updateFilters,
    clearFilters,

    // Search
    searchQuery,
    updateSearch,

    // Pagination
    pagination,
    loadMore,
    resetPagination,
    hasNextPage,
    currentPage,
    totalPages,

    // Actions
    refetch,
  };
};

/**
 * Single Product ViewModel - For product detail pages
 */
export const useProductViewModel = (productId: string) => {
  // State for region_id from runtime config
  const [regionId, setRegionId] = useState<string | undefined>();

  // Load region_id from runtime config
  useEffect(() => {
    fetchRuntimeConfig().then((config) => {
      if (config.medusa.regionId) {
        setRegionId(config.medusa.regionId);
      }
    }).catch((error) => {
      console.error('Failed to load region from runtime config:', error);
    });
  }, []);

  const {
    data: productData,
    isLoading,
    error,
    refetch,
  } = useGetProductQuery({ id: productId, region_id: regionId }, {
    skip: !productId || !regionId,
  });

  const product = productData?.product;

  // Get available variants
  const variants = useMemo(() => {
    return product?.variants || [];
  }, [product]);

  // Get price range
  const priceRange = useMemo(() => {
    if (!variants.length) return null;

    const prices = variants
      .map(variant => variant.calculated_price?.calculated_amount)
      .filter((price): price is number => typeof price === 'number' && price > 0);

    if (prices.length === 0) return null;

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }, [variants]);

  // Get default variant (first available)
  const defaultVariant = useMemo(() => {
    return variants.find(variant => variant.inventory_quantity > 0) || variants[0];
  }, [variants]);

  return {
    product,
    variants,
    defaultVariant,
    priceRange,
    isLoading,
    error,
    refetch,
  };
};